import React from "react";
import { convertDateFormatWithOutTime } from "../Utils/commonFunction";
import "./PolicyCard.css";

/* ============================================================================
   PolicyCard — one policy, as an object rather than a table.

   The old card was a bordered <Table> of label/value rows with the policy
   number in the header. Every row carried equal weight, so nothing answered
   the two questions a customer actually opens this app with: what is this, and
   is it still live.

   This card answers both before anything else. The COVERAGE RAIL across the
   top is the same device the sign-in screen uses — a span of time running out
   — and here it is literal: how much of this policy's cover has elapsed.
   ============================================================================ */

/** Indian digit grouping. The raw values run to six figures and read as noise
 *  without separators. */
const formatINR = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const DAY = 1000 * 60 * 60 * 24;

/**
 * Where this policy sits in its own term.
 *
 * `isPolicyExpired` comes from the API and stays the authority on expired vs
 * not. The day count is derived here purely to say something more useful than
 * "Active" when a policy has three weeks left.
 */
const coverage = (item) => {
  const start = item.policyStartDate ? new Date(item.policyStartDate) : null;
  const end = item.policyEndDate ? new Date(item.policyEndDate) : null;
  const now = new Date();

  let percent = item.isPolicyExpired ? 100 : 0;
  if (start && end && end > start) {
    percent = Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
  }

  const daysLeft = end ? Math.ceil((end - now) / DAY) : null;

  if (item.isPolicyExpired) {
    return { percent: 100, tone: "expired", label: "Expired" };
  }
  if (daysLeft !== null && daysLeft <= 30) {
    return {
      percent,
      tone: "ending",
      label: daysLeft <= 1 ? "Ends today" : `${daysLeft} days left`,
    };
  }
  return { percent, tone: "active", label: "Active" };
};

const PolicyCard = ({
  item,
  documentUrl,
  onViewPolicy,
  onSendPolicy,
  onCourier,
  onEndorsement,
  onClaim,
}) => {
  const cover = coverage(item);

  return (
    <article className={`pcard pcard--${cover.tone}`}>
      {/* The coloured band carrying the policy's identity and its state. This
          is what stops a page of cards reading as a page of white boxes. */}
      <header className="pcard__head">
        <div className="pcard__headings">
          {/* What it IS leads. The number identifies it, but nobody thinks of
              a policy by its number first. Both carry their label: on a wallet
              of several policies "Mediclaim" and "TESTFINAL" are otherwise two
              unexplained strings stacked on each other.

              The colon is the boundary. "POLICY NUMBER TESTF" ran the label
              straight into the value, and policy numbers here look like TESTF,
              TESTFINAL and 4216-400202-26-7026615-07 -- so a customer had no way
              to tell where the caption stopped and their own number began.
              Claim, Courier and Endorsement already render "Policy Number : x";
              this card was the one place that did not.

              Two lines, not one. The pair fits on a desktop and does not fit a
              320px phone, where "Top Up Long Term" beside a 25-character policy
              number either wraps mid-number or truncates the thing the customer
              came to read. */}
          <p className="pcard__label">Policy Type :</p>
          <h3 className="pcard__type">{item.policyTypeName || "—"}</h3>
          <p className="pcard__no">
            <span>Policy Number :</span> {item.policyNo || "—"}
          </p>
          {item.previousPolicyNo && (
            <p className="pcard__prev">Renewed from : {item.previousPolicyNo}</p>
          )}
        </div>
        <span className="pcard__chip">{cover.label}</span>
      </header>

      {/* THE COVERAGE RAIL — the same device as the sign-in screen, and here
          doing its literal job: the share of the term already used. */}
      <div
        className="pcard__rail"
        role="img"
        aria-label={`Cover ${Math.round(cover.percent)} percent elapsed`}
      >
        <span className="pcard__rail-fill" style={{ width: `${cover.percent}%` }} />
      </div>

      <div className="pcard__body">
        {/* The term, as two dated ends rather than one run-on line. */}
        <div className="pcard__term">
          <div className="pcard__term-end">
            <span className="pcard__term-label">Cover from</span>
            <span className="pcard__term-date">
              {convertDateFormatWithOutTime(item.policyStartDate)}
            </span>
          </div>
          <i className="fa fa-arrow-right pcard__term-arrow" aria-hidden="true"></i>
          <div className="pcard__term-end">
            <span className="pcard__term-label">Cover to</span>
            <span className="pcard__term-date">
              {convertDateFormatWithOutTime(item.policyEndDate)}
            </span>
          </div>
        </div>

        {/* Every field the old table carried, none of them conditional — an
            empty remark shows a dash rather than silently dropping the row and
            leaving two cards side by side at different heights. */}
        <dl className="pcard__facts">
          <dt>Insured</dt>
          <dd>{item.policyHolderName || <span className="pcard__muted">—</span>}</dd>

          <dt>Company</dt>
          <dd>{item.insuranceCompanyName || <span className="pcard__muted">—</span>}</dd>

          <dt>Net premium</dt>
          <dd>
            <span className="pcard__amount">₹{formatINR(item.netAmount)}</span>
            <span className="pcard__breakdown">
              ₹{formatINR(item.premiumAmt)} + ₹{formatINR(item.serviceTaxAmount)} GST
              {item.serviceTax ? ` (${item.serviceTax}%)` : ""}
            </span>
          </dd>

          <dt>Remark</dt>
          <dd>{item.externalRemarks || <span className="pcard__muted">—</span>}</dd>
        </dl>
      </div>

      {item.imagePath && (
        <div className="pcard__docs">
          <a
            className="pcard__doc-link"
            href={documentUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onViewPolicy}
          >
            <i className="fa fa-file-pdf" aria-hidden="true"></i>
            View policy
          </a>
          <button
            type="button"
            className="pcard__doc-link pcard__doc-link--send"
            onClick={onSendPolicy}
          >
            <i className="fa fa-paper-plane" aria-hidden="true"></i>
            Send to me
          </button>
        </div>
      )}

      <footer className="pcard__foot">
        <button type="button" className="pcard__action" onClick={onCourier}>
          <i className="fa fa-truck-fast" aria-hidden="true"></i>
          Courier
        </button>
        <button type="button" className="pcard__action" onClick={onEndorsement}>
          <i className="fa fa-file-pen" aria-hidden="true"></i>
          Endorsement
        </button>
        <button type="button" className="pcard__action" onClick={onClaim}>
          <i className="fa fa-clipboard-check" aria-hidden="true"></i>
          Claim
        </button>
      </footer>
    </article>
  );
};

export default PolicyCard;
